import { AnimatePresence, motion } from 'framer-motion'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { useEffect, useState } from 'react'

export interface Story {
  image: string
  title: string
  subtitle: string
  description: string
  mood: string
}

interface StoryCarouselProps {
  stories: Story[]
  themeColor: string
  interval?: number
  className?: string
  resetTrigger?: number
}

export function StoryCarousel({ 
  stories, 
  themeColor, 
  interval = 4000,
  className = '',
  resetTrigger = 0
}: StoryCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  
  const currentStory = stories[currentIndex]

  // Handle auto-rotation
  useEffect(() => {
    if (isPaused) return

    const intervalId = window.setInterval(() => {
      setCurrentIndex((c) => (c + 1) % stories.length)
    }, interval)

    return () => clearInterval(intervalId)
  }, [currentIndex, interval, isPaused, stories.length])

  // Reset when resetTrigger changes
  useEffect(() => {
    setCurrentIndex(0)
  }, [resetTrigger])

  // Reset progress when index changes manually
  const handleManualChange = (newIndex: number) => {
    setIsPaused(true)
    setCurrentIndex(newIndex)
    window.setTimeout(() => setIsPaused(false), interval)
  }

  const nextSlide = () => {
    handleManualChange((currentIndex + 1) % stories.length)
  }

  const prevSlide = () => {
    handleManualChange((currentIndex - 1 + stories.length) % stories.length)
  }

  return (
    <div 
      className={`relative group ${className}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => window.setTimeout(() => setIsPaused(false), interval)}
    >
      {/* Main Card */}
      <div className="relative bg-white rounded-[2rem] shadow-2xl overflow-hidden border border-slate-100 transition-all duration-500 hover:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)]">
        
        <div className="grid md:grid-cols-2 min-h-[620px] md:min-h-[500px]">
          {/* Image Section */}
          <div className="relative bg-slate-50 flex items-center justify-center p-6 sm:p-8 md:p-12 overflow-hidden min-h-[260px] sm:min-h-[320px] md:min-h-[500px]">
            {/* Decorative Background Blob */}
            <div 
              className="absolute w-[120%] h-[120%] rounded-full opacity-10 blur-3xl transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2"
              style={{ backgroundColor: themeColor }}
            />
            
            <AnimatePresence mode="wait">
              <motion.img
                key={`img-${currentIndex}`}
                src={currentStory.image}
                alt={currentStory.title}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="relative z-10 w-full max-w-[220px] sm:max-w-[280px] md:max-w-sm h-[210px] sm:h-[260px] md:h-[360px] object-contain drop-shadow-xl"
              />
            </AnimatePresence>
          </div>

          {/* Content Section */}
          <div className="relative p-6 sm:p-8 md:p-12 flex flex-col justify-center min-h-[360px] sm:min-h-[330px] md:min-h-[400px]">
            <AnimatePresence mode="wait">
              <motion.div 
                key={`text-${currentIndex}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="flex flex-col"
              >
                <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4 md:mb-6 w-fit min-h-6"
                  style={{ 
                    backgroundColor: `${themeColor}15`,
                    color: themeColor 
                  }}
                >
                  {currentStory.subtitle}
                </div>

                <h3 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4 md:mb-6 leading-tight min-h-[3.5rem] md:min-h-[6.5rem]">
                  {currentStory.title}
                </h3>

                <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 md:mb-8 min-h-[8.5rem] sm:min-h-[7rem] md:min-h-[8.75rem]">
                  {currentStory.description}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Controls */}
            <div className="flex items-center justify-between gap-4 mt-auto pt-6 md:pt-8 border-t border-slate-100">
              <div className="flex gap-2 flex-wrap max-w-[160px] sm:max-w-none">
                {stories.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleManualChange(idx)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      idx === currentIndex ? 'w-8' : 'w-2 bg-slate-200 hover:bg-slate-300'
                    } touch-manipulation`}
                    style={idx === currentIndex ? { backgroundColor: themeColor } : {}}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={prevSlide}
                  className="p-3 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors hover:border-slate-300 touch-manipulation"
                  aria-label="Previous slide"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextSlide}
                  className="p-3 rounded-full text-white shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 hover:-translate-y-0.5 transition-all touch-manipulation"
                  style={{ backgroundColor: themeColor }}
                  aria-label="Next slide"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
